import type {
  AnimationCtx,
  AnimationModeOpts,
  AutoRefreshCtx,
  TimeContextUnion,
} from "@/models/time-control/timeStateCtxTypes";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import type { StateFrom, StateMachine } from "xstate";

export enum TimeStates {
  ANIMATION = "ANIMATION",
  AUTO_REFRESH = "AUTO_REFRESH",
}

// Typing for the state and context shape:
// https://xstate.js.org/docs/guides/typescript.html#typestates
export type TimeTypestate =
  | {
      value: TimeStates.ANIMATION;
      context: AnimationCtx & { state: TimeStates.ANIMATION };
    }
  | {
      value: TimeStates.AUTO_REFRESH;
      context: AutoRefreshCtx & { state: TimeStates.AUTO_REFRESH };
    };

/**
 * Event types to trigger transitions between states
 */
export enum TransitionEventType {
  setAutoRefreshMode = "setAutoRefreshMode",
  initAutoRefreshMode = "initAutoRefreshMode",
  initAnimationMode = "initAnimationMode",
  setAnimationMode = "setAnimationMode",
}

/**
 * Event types to trigger self transition within a single state
 */
export enum SelfTransitionEventType {
  refreshDisplayTime = "refreshDisplayTime", // For auto refresh mode
  updateDisplayTime = "updateDisplayTime",
  updateNextDisplayTime = "updateNextDisplayTime",
  updateGuiTimezone = "updateGuiTimezone",
  // updates only the modeOpts inside animation context
  updateAnimationMode = "updateAnimationMode",
  // used to update all times (displayTime, nextDisplayTime_, animationTime_)
  updateFullAnimationConfig = "updateFullAnimationConfig",
  recomputeAutoRefreshTime = "recomputeAutoRefreshTime",
  updateRefreshRate = "updateRefreshRate",
  toggleLockMode = "toggleLockMode",
  autoMoveInTime = "autoMoveInTime",
}

export type TimeEventUnion =
  | { type: TransitionEventType.setAutoRefreshMode }
  | { type: TransitionEventType.initAutoRefreshMode }
  | { type: TransitionEventType.initAnimationMode }
  | { type: SelfTransitionEventType.refreshDisplayTime }
  | {
      type: TransitionEventType.setAnimationMode;
    }
  | {
      type: SelfTransitionEventType.updateAnimationMode;
      modeOpts: AnimationModeOpts;
    }
  | {
      type: SelfTransitionEventType.updateFullAnimationConfig;
      displayTime: DateTime;
      nextDisplayTime: DateTime;
    }
  | {
      type: SelfTransitionEventType.recomputeAutoRefreshTime;
    }
  | {
      type: SelfTransitionEventType.updateDisplayTime;
      displayTime: DateTime;
    }
  | {
      type: SelfTransitionEventType.updateNextDisplayTime;
      nextDisplayTime_: DateTime;
    }
  | {
      type: SelfTransitionEventType.updateGuiTimezone;
      timezone: GuiTimeZone;
    }
  | {
      type: SelfTransitionEventType.toggleLockMode;
    }
  | {
      type: SelfTransitionEventType.updateRefreshRate;
      refreshRate: number;
    }
  | {
      type: SelfTransitionEventType.autoMoveInTime;
    };

export type TimeStateMachine = StateMachine<TimeContextUnion, any, TimeEventUnion, TimeTypestate>;
export type TimeState = StateFrom<TimeStateMachine>;
